import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { realtimeService } from '../services/realtimeService';
import { isAuthSessionInvalid, forceSessionRelogin } from '../utils/sessionAuth';

// Create Auth Context
const AuthContext = createContext();

// Auth Provider Component
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [impersonation, setImpersonation] = useState(null);
  const [stoppingImpersonation, setStoppingImpersonation] = useState(false);
  const [token, setToken] = useState(localStorage.getItem('token'));
  const [loading, setLoading] = useState(true);

  // Configure axios defaults with dynamic domain detection and check auth
  useEffect(() => {
    // Set auth header immediately if we have a token (e.g. after login or page refresh)
    if (token) {
      axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
      realtimeService.setToken(token);
    } else {
      delete axios.defaults.headers.common['Authorization'];
      realtimeService.setToken(null);
    }

    const loadDomainsAndConfigureApi = async () => {
      const currentOrigin = typeof window !== 'undefined' ? window.location.origin : '';
      
      // Get base API URL: use same origin when page is HTTP so we don't force HTTPS (e.g. before SSL is set up)
      const isLocalhost = currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1');
      let baseApiUrl = process.env.REACT_APP_API_URL || (isLocalhost ? 'http://localhost:3001' : '');
      const pageIsHttps = currentOrigin.startsWith('https');
      const envPointsToHttps = baseApiUrl.startsWith('https');
      if (!isLocalhost && !pageIsHttps && envPointsToHttps) {
        baseApiUrl = ''; // same origin = HTTP, so /api goes to current host over HTTP
      }
      // Remove /api suffix if present since routes already include /api/ prefix
      const apiUrl = baseApiUrl.endsWith('/api') ? baseApiUrl.replace(/\/api$/, '') : baseApiUrl;
      
      // Set axios baseURL first so all subsequent calls use it
      axios.defaults.baseURL = apiUrl;

      // Try to load domains from API first
      let domains = [];
      try {
        // Load domains from API (no auth required for initial setup)
        // Use axios now that baseURL is set
        const response = await axios.get('/api/domains');
        
        if (response.data && response.data.domains) {
          domains = response.data.domains || [];

          // Cache domains in localStorage
          localStorage.setItem('workmanager_domains', JSON.stringify(domains));
        } else {
          console.warn('Failed to load domains from API, using cached or fallback');
          // Try to load from cache
          const cached = localStorage.getItem('workmanager_domains');
          if (cached) {
            try {
              domains = JSON.parse(cached);
            } catch (e) {
              console.warn('Failed to parse cached domains');
            }
          }
        }
      } catch (error) {
        console.warn('Failed to load domains from API, using cached or fallback:', error);
        // Try to load from cache
        const cached = localStorage.getItem('workmanager_domains');
        if (cached) {
          try {
            domains = JSON.parse(cached);
          } catch (e) {
            console.warn('Failed to parse cached domains');
          }
        }
      }

      // Find the current domain in our domain list (normalize for comparison)
      const normalizeDomain = (domain) => {
        return domain.replace(/^https?:\/\//, '').toLowerCase();
      };
      const normalizedCurrentOrigin = normalizeDomain(currentOrigin);
      const currentDomain = domains.find(d => {
        const normalizedDomainName = normalizeDomain(d.domain_name);
        return normalizedDomainName === normalizedCurrentOrigin;
      });

      // API URL: same origin as the page so login/auth go to the host the user is on
      let finalApiUrl = apiUrl;
      if (currentOrigin.includes('localhost') || currentOrigin.includes('127.0.0.1')) {
        finalApiUrl = 'http://localhost:3001';
      } else if (currentDomain && currentDomain.is_active) {
        finalApiUrl = '';
      } else {
        // Current host not in domain list (e.g. fabworks.lightring.org): use same origin, not primary domain
        finalApiUrl = '';
      }

      // Update the base URL if it changed
      if (finalApiUrl !== apiUrl) {
        axios.defaults.baseURL = finalApiUrl;
      }

      // Optional: set window.__WORKMANAGER_DEBUG__ = true to show API/domain logs
      if (typeof window !== 'undefined' && window.__WORKMANAGER_DEBUG__) {
        const displayUrl = axios.defaults.baseURL || '(same origin)';
        console.log('🌐 Dynamic API URL detected:', displayUrl);
        console.log('📍 Current origin:', currentOrigin);
        console.log('📋 Available domains:', domains.map(d => d.domain_name));
      }

      // Set auth header if token exists
      if (token) {
        axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
        realtimeService.setToken(token);
        
        // Now check if user is authenticated
        try {
          const response = await axios.get('/api/auth/me');
          const data = response.data.data;
          const { impersonation: imp, ...employee } = data;
          setUser(employee);
          setImpersonation(imp || null);
        } catch (error) {
          if (isAuthSessionInvalid(error)) {
            setUser(null);
            setToken(null);
            const inactive = error.response?.data?.error === 'Account inactive';
            forceSessionRelogin(inactive ? 'inactive' : 'expired');
            return;
          }
          console.error('Auth check failed:', error);
          setUser(null);
        }
      } else {
        delete axios.defaults.headers.common['Authorization'];
        realtimeService.setToken(null);
      }
      
      setLoading(false);
    };

    loadDomainsAndConfigureApi();
  }, [token]);

  // Login function
  const login = async (email, password) => {
    try {
      const response = await axios.post('/api/auth/login', { email, password });
      const { employee, token: newToken } = response.data.data;

      setUser(employee);
      setImpersonation(null);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      // Set axios auth header immediately so subsequent requests (e.g. Dashboard) include the token
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      realtimeService.setToken(newToken);

      return { success: true };
    } catch (error) {
      console.error('Login failed:', error);
      const msg = error.response?.data?.message || error.response?.data?.error;
      if (error.response?.status === 401 || msg?.toLowerCase().includes('invalid') || msg?.toLowerCase().includes('incorrect')) {
        return { success: false, message: 'Invalid email or password. Please check and try again.' };
      }
      if (error.response?.status >= 400 && msg) return { success: false, message: msg };
      return { success: false, message: 'Unable to sign in. Check your connection and try again.' };
    }
  };

  // Logout: record server-side audit event, then clear local session
  const logout = async () => {
    try {
      if (token) {
        await axios.post('/api/auth/logout');
      }
    } catch {
      // Still clear session if logout request fails (e.g. network)
    }
    setUser(null);
    setImpersonation(null);
    setToken(null);
    localStorage.removeItem('token');
    delete axios.defaults.headers.common['Authorization'];
    realtimeService.setToken(null);
  };

  // Check if user is authenticated
  const isAuthenticated = !!user && !!token;

  // Check if user has specific role
  const hasRole = (role) => {
    return user?.role === role;
  };

  // Check if user is admin
  const isAdmin = () => hasRole('admin') || hasRole('enterprise_admin');

  const isEnterpriseAdmin = () => hasRole('enterprise_admin');

  // Check if user is employee, manager, estimator, or admin
  const isEmployeeOrAdmin = () =>
    hasRole('employee') ||
    hasRole('admin') ||
    hasRole('enterprise_admin') ||
    hasRole('manager') ||
    hasRole('estimator');

  // Check if user is manager or admin (for inventory write actions, etc.)
  const isManagerOrAdmin = () => hasRole('manager') || hasRole('admin') || hasRole('enterprise_admin');

  const isEstimator = () => hasRole('estimator');

  const stopImpersonation = async () => {
    setStoppingImpersonation(true);
    try {
      const response = await axios.post('/api/auth/stop-impersonation');
      const { user: restoredUser, token: newToken } = response.data.data;
      setUser(restoredUser);
      setImpersonation(null);
      setToken(newToken);
      localStorage.setItem('token', newToken);
      axios.defaults.headers.common['Authorization'] = `Bearer ${newToken}`;
      realtimeService.setToken(newToken);
      window.location.href = '/admin';
    } catch (error) {
      console.error('Stop impersonation failed:', error);
    } finally {
      setStoppingImpersonation(false);
    }
  };

  const value = {
    user,
    token,
    loading,
    impersonation,
    stoppingImpersonation,
    isAuthenticated,
    login,
    logout,
    stopImpersonation,
    hasRole,
    isAdmin,
    isEnterpriseAdmin,
    isEmployeeOrAdmin,
    isManagerOrAdmin,
    isEstimator
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};
